// =============================================================================
// domain/bulkEdit.ts — what one press of "Apply changes to 63 trades" writes.
// -----------------------------------------------------------------------------
// The most destructive control in the product. One click patches every selected
// trade, and the selection is routinely the whole Needs-Review queue.
//
// ── 🚨 THE SAFETY PROPERTY: A SECTION THAT IS OFF CONTRIBUTES NO KEY ──
//
// Not "contributes an empty value" — NO KEY AT ALL. The old client's modal opens
// with every section unchecked and the checkbox IS the opt-in, which is what
// makes "leave this field alone" and "set this field to empty" distinguishable.
// Collapse that distinction and one careless press blanks a field across the
// whole selection, authorised and irreversible.
//
// Every test in this file's suite exists to hold that line. `bulkPatchFor`
// returns the KEYS to write, so an absent key is a column that is not in the
// UPDATE at all.
//
// ── 🚨 WHY THIS CLIENT CAN BULK-EDIT AT ALL, GIVEN D46 ──
//
// D46 says: never save a row you did not fully read. Bulk edit touches trades the
// user never opened, so by that rule it should be impossible.
//
// It is legal because this client does NOT write rows here — it writes COLUMNS.
// `update({pattern_id: …}).in('id', ids)` names the columns it sets and cannot
// touch the other 45. The old client cannot do this: it holds whole trades in
// memory and saves whole trades, so its bulk edit is a row write that happens to
// be safe only because its rows are complete.
//
// ⚠ That is the whole argument, and it fails the moment someone "optimises" this
// into an upsert of assembled row objects. The projection is 23 of 48 columns;
// an upsert would silently destroy the other 25 on every selected trade.
//
// ── THE DEFAULT IS OVERWRITE ──
//
// The old client's comment: *"ALL DEFAULT MODES ARE 'OVERWRITE' — matches the
// user's mental model of 'Bulk Edit means edit, not patch'."* Each section then
// offers ONE preserve toggle whose meaning varies by field type: set-only-if-blank
// for the scalars, add-to-existing for the lists, append for notes.
// =============================================================================

/** The six discipline breaches, plus the two that are not discipline. */
export const BULK_BOOLEAN_KEYS = [
  'planNotFollowed', 'stopNotHonored', 'targetNotHonored',
  'revengeTrade', 'fomoTrade', 'overtraded',
  // ⚠ Setup QUALITY, not discipline — the old client excludes it from the
  // discipline grade. Editable in bulk, but never read as a breach.
  'subAPlus',
  // ⚠ LOSS-ONLY in the old client's form. Not enforced here: the domain does not
  // know a trade's P&L sign, and inventing the rule at this layer would make bulk
  // and single-trade editing disagree about the same field.
  'controllableLoss',
] as const;
export type BulkBooleanKey = (typeof BULK_BOOLEAN_KEYS)[number];

/**
 * What to do with one discipline flag.
 *
 * ⚠ THREE STATES, AND `clear` IS NOT `false`. An unchecked breach box is
 * DELETED, never stored as `false` — because a stored `false` is a positive claim
 * ("this went right") that the user never made. `skip` leaves whatever is there.
 */
export type BooleanAction = 'skip' | 'set' | 'clear';

export interface BulkSection<T> {
  on: boolean;
  value: T;
  /**
   * The single preserve toggle. Meaning is per-section and stated at the use:
   * scalars → set only if currently blank · lists → add to existing · notes →
   * append. `false` is OVERWRITE, which is the default.
   */
  preserve?: boolean;
}

export interface BulkEditPlan {
  pattern: BulkSection<string>;
  exitModel: BulkSection<string>;
  grade: BulkSection<string>;
  mistakeTags: BulkSection<readonly string[]>;
  notes: BulkSection<string>;
  riskPoints: BulkSection<number>;
  rrPlanned: BulkSection<number>;
  account: BulkSection<string>;
  booleans: { on: boolean; states: Readonly<Record<string, BooleanAction>> };
  /** ⚠ DEFAULT ON in the old client — "bulk-editing IS annotation in spirit". */
  annotated: { on: boolean };
}

export function emptyBulkPlan(): BulkEditPlan {
  const off = <T>(value: T): BulkSection<T> => ({ on: false, value, preserve: false });
  return {
    pattern: off(''), exitModel: off(''), grade: off(''),
    mistakeTags: off([] as readonly string[]),
    notes: off(''), riskPoints: off(0), rrPlanned: off(0), account: off(''),
    booleans: { on: false, states: {} },
    // ⚠ ON. Matches the old client, whose own copy explains it: bulk-editing IS
    // annotating — the trades flip to annotated and leave the Unannotated Queue.
    annotated: { on: true },
  };
}

/** What the patch computation needs to read off a trade. */
export interface BulkEditTrade {
  id: string;
  patternId?: string | null;
  exitModelId?: string | null;
  grade?: string | null;
  mistakeTags?: readonly string[] | null;
  notes?: string | null;
  riskPoints?: number | null;
  rrPlanned?: number | null;
  accountId?: string | null;
  booleans?: Readonly<Record<string, unknown>> | null;
}

const blankStr = (v: unknown): boolean => !String(v ?? '').trim();
const blankNum = (v: unknown): boolean => !(Number(v) > 0);

/**
 * The COLUMN PATCH for one trade, or `null` when this plan changes nothing on it.
 *
 * ⚠ Returns database column names, not model fields, because the caller issues an
 * `update()` with exactly these keys. Keeping the mapping here means the writer
 * has no chance to add a column of its own.
 *
 * ⚠ `null` rather than `{}` for "no change": an empty object handed to
 * `update()` is an error in some drivers and a no-op write in others, and a
 * no-op write still bumps `updated_at` — which would make every other client's
 * next save look like a conflict.
 */
export function bulkPatchFor(
  t: BulkEditTrade,
  plan: BulkEditPlan,
): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};

  // ── Scalars. `preserve` = set only where the trade is currently blank. ──
  if (plan.pattern.on && !(plan.pattern.preserve && !blankStr(t.patternId))) {
    patch['pattern_id'] = plan.pattern.value || null;
  }
  if (plan.exitModel.on && !(plan.exitModel.preserve && !blankStr(t.exitModelId))) {
    patch['exit_model_id'] = plan.exitModel.value || null;
  }
  if (plan.grade.on && !(plan.grade.preserve && !blankStr(t.grade))) {
    patch['grade'] = plan.grade.value;
  }
  if (plan.riskPoints.on && !(plan.riskPoints.preserve && !blankNum(t.riskPoints))) {
    patch['risk_points'] = plan.riskPoints.value;
  }
  if (plan.rrPlanned.on && !(plan.rrPlanned.preserve && !blankNum(t.rrPlanned))) {
    patch['rr_planned'] = plan.rrPlanned.value;
  }
  if (plan.account.on && plan.account.value) {
    patch['account_id'] = plan.account.value;
  }

  // ── Mistake tags. `preserve` = ADD to existing rather than replace. ──
  if (plan.mistakeTags.on) {
    const chosen = [...plan.mistakeTags.value];
    if (plan.mistakeTags.preserve) {
      // ⚠ Union, ORDER-STABLE: existing first, then the new ones not already
      // present. A Set round-trip would reorder a user's own tag order for no
      // reason and make the diff unreadable.
      const have = [...(t.mistakeTags ?? [])];
      const seen = new Set(have);
      patch['mistake_tags'] = [...have, ...chosen.filter((x) => !seen.has(x))];
    } else {
      patch['mistake_tags'] = chosen;
    }
  }

  // ── Notes. `preserve` = APPEND. ──
  if (plan.notes.on) {
    const incoming = plan.notes.value;
    if (plan.notes.preserve) {
      const have = String(t.notes ?? '');
      // ⚠ Appending to an empty note must not leave a leading blank line.
      patch['notes'] = have.trim() ? `${have}\n\n${incoming}` : incoming;
    } else {
      patch['notes'] = incoming;
    }
  }

  // ── Discipline flags. Merged over what is there; `clear` DELETES. ──
  if (plan.booleans.on) {
    const next: Record<string, unknown> = { ...(t.booleans ?? {}) };
    let touched = false;
    for (const [key, action] of Object.entries(plan.booleans.states)) {
      if (action === 'skip') continue;
      touched = true;
      // ⚠ `delete`, NOT `= false`. A stored false is a positive claim that this
      // went right, and bulk-clearing a flag must not manufacture one across the
      // whole selection. Same asymmetry the annotation editor honours.
      if (action === 'clear') delete next[key];
      else next[key] = true;
    }
    if (touched) patch['booleans'] = next;
  }

  if (plan.annotated.on) patch['annotated'] = true;

  return Object.keys(patch).length ? patch : null;
}

/** Is any section switched on? A plan with nothing on must not be appliable. */
export function bulkPlanIsEmpty(plan: BulkEditPlan): boolean {
  if (plan.annotated.on) return false;
  if (plan.booleans.on
    && Object.values(plan.booleans.states).some((a) => a !== 'skip')) return false;
  return !(
    plan.pattern.on || plan.exitModel.on || plan.grade.on || plan.mistakeTags.on
    || plan.notes.on || plan.riskPoints.on || plan.rrPlanned.on || plan.account.on
  );
}

export interface OverwriteWarning {
  field: string;
  count: number;
}

/**
 * Which enabled sections would OVERWRITE data that already exists, and on how
 * many trades.
 *
 * ⚠ Drives a confirmation the user must click through — the old client forces it
 * for exactly this set. Silence here is the difference between "I re-tagged 63
 * untagged trades" and "I destroyed the pattern on 40 trades I had already done".
 *
 * ⚠ Counts trades whose existing value DIFFERS from the incoming one. Re-applying
 * the same value to a trade that already has it is not an overwrite worth
 * warning about, and warning anyway trains the user to click through.
 */
export function bulkOverwrites(
  trades: readonly BulkEditTrade[],
  plan: BulkEditPlan,
): OverwriteWarning[] {
  const out: OverwriteWarning[] = [];
  const scalar = (
    on: boolean, preserve: boolean | undefined, field: string,
    read: (t: BulkEditTrade) => unknown, incoming: unknown,
  ) => {
    if (!on || preserve) return;
    const n = trades.filter((t) => {
      const cur = read(t);
      return !blankStr(cur) && String(cur) !== String(incoming);
    }).length;
    if (n) out.push({ field, count: n });
  };

  scalar(plan.pattern.on, plan.pattern.preserve, 'Pattern', (t) => t.patternId, plan.pattern.value);
  scalar(plan.exitModel.on, plan.exitModel.preserve, 'Exit Model', (t) => t.exitModelId, plan.exitModel.value);
  scalar(plan.grade.on, plan.grade.preserve, 'Grade', (t) => t.grade, plan.grade.value);

  if (plan.riskPoints.on && !plan.riskPoints.preserve && plan.riskPoints.value > 0) {
    const n = trades.filter((t) => !blankNum(t.riskPoints)
      && Number(t.riskPoints) !== plan.riskPoints.value).length;
    if (n) out.push({ field: 'Risk (pts)', count: n });
  }
  if (plan.rrPlanned.on && !plan.rrPlanned.preserve && plan.rrPlanned.value > 0) {
    const n = trades.filter((t) => !blankNum(t.rrPlanned)
      && Number(t.rrPlanned) !== plan.rrPlanned.value).length;
    if (n) out.push({ field: 'R:R Planned', count: n });
  }
  if (plan.mistakeTags.on && !plan.mistakeTags.preserve && plan.mistakeTags.value.length) {
    const n = trades.filter((t) => (t.mistakeTags ?? []).length > 0).length;
    if (n) out.push({ field: 'Mistake tags', count: n });
  }
  if (plan.notes.on && !plan.notes.preserve && plan.notes.value.trim()) {
    const n = trades.filter((t) => String(t.notes ?? '').trim()).length;
    if (n) out.push({ field: 'Notes', count: n });
  }
  if (plan.account.on && plan.account.value) {
    const n = trades.filter((t) => t.accountId && t.accountId !== plan.account.value).length;
    if (n) out.push({ field: 'Account', count: n });
  }
  return out;
}
